import { useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import API from "../../../util/API";
import AdminPageShell from "../components/AdminPageShell";
import ADMIN_PATHS from '../ADMIN_PATHS';

const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const resolveProfilePic = (src) => {
  if (!src) return '';
  const raw = String(src);
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;

  const base = String(API?.defaults?.baseURL || '').replace(/\/$/, '');
  if (base) {
    const origin = base.endsWith('/api') ? base.slice(0, -4) : base;
    return `${origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
  }

  return `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
};

function AdminList () {
  const [admins, setAdmins] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const navigate = useNavigate();
  const getadmins = async () => {
    try {
      setLoading(true)
      const getadminsData = await API.get("/admin");
      setAdmins(getadminsData.data.data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getadmins()
  },[])

  const filteredAdmins = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return admins || [];
    return (admins || []).filter((a) => {
      const username = String(a?.username || a?.name || '').toLowerCase();
      const email = String(a?.email || '').toLowerCase();
      return username.includes(q) || email.includes(q);
    });
  }, [admins, search]);

    return(
      <AdminPageShell
        title="Admin List"
        description="View and manage administrator accounts."
        chips={
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
            <span className="font-semibold text-slate-900">{filteredAdmins?.length || 0}</span>&nbsp;admins
          </span>
        }
      >
        <Card className="p-4 mb-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="flex-1">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email…"
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30"
              />
            </div>
            <div className="shrink-0">
              <button
                type="button"
                onClick={() => navigate(ADMIN_PATHS.ADMIN.CREATE)}
                className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:text-[#D2042D] transition-colors"
              >
                Create Admin
              </button>
            </div>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-black tracking-wide text-slate-600 uppercase">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Phone</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Designation</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="py-10 text-center text-sm text-slate-500">
                      Loading admins…
                    </td>
                  </tr>
                ) : filteredAdmins?.length ? (
                  filteredAdmins.map((admin) => (
                    <tr key={admin._id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {admin?.profilePic ? (
                            <img
                              src={resolveProfilePic(admin.profilePic)}
                              alt={admin?.username || admin?.email || 'Admin'}
                              className="h-9 w-9 rounded-full object-cover border border-slate-200"
                            />
                          ) : (
                            <div className="h-9 w-9 rounded-full bg-[#D2042D]/10 text-[#D2042D] flex items-center justify-center text-sm font-black">
                              {String(admin?.username || admin?.name || admin?.email || 'A').charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 truncate">
                              {admin.username || admin.name || 'ADMIN'}
                            </div>
                            <div className="text-xs text-slate-500 truncate">{admin._id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{admin.email}</td>
                      <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{admin.phone || '—'}</td>
                      <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{admin.designation || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-bold text-slate-700">
                          {admin?.roleId?.name || admin.role || "Unassigned"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => navigate(`${ADMIN_PATHS.ADMIN.VIEW}/${admin._id}`)}
                          className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-[#D2042D] transition-colors"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="py-10 text-center text-sm text-slate-500">
                      No admins found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </AdminPageShell>
    )
}

export default AdminList;